export type PinnedSide = 'left' | 'right' | null;

export interface StoredPin {
  colId: string;
  pinned: PinnedSide;
}

const KEY_PREFIX = 'bombiix:gridpins';

/**
 * Builds the localStorage key that scopes pinned-column state to a user and a specific grid,
 * so one user's Sample To-Do pins never leak into another grid or another user.
 */
export function buildPinKey(userId: string | undefined, persistKey: string): string {
  return `${KEY_PREFIX}:${userId ?? 'anon'}:${persistKey}`;
}

/**
 * Reads persisted pinned-column state, returning an empty list when the entry is absent,
 * not an array, or corrupt. Never throws — a bad value degrades to default column layout.
 */
export function readPins(key: string): StoredPin[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as unknown[]).filter((entry): entry is StoredPin => typeof (entry as StoredPin)?.colId === 'string');
  } catch {
    return [];
  }
}

/**
 * Persists pinned-column state, silently degrading to session-only behaviour on any storage
 * error (quota exceeded, private-browsing). Pinning still works for the session, just is not saved.
 */
export function writePins(key: string, pins: StoredPin[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(pins));
  } catch {
    
  }
}

/**
 * Returns a new list with the given column's pin state upserted, preserving all other entries.
 * An explicit `null` records a deliberate unpin so it overrides a column's default pin.
 */
export function upsertPin(pins: StoredPin[], colId: string, pinned: PinnedSide): StoredPin[] {
  const next = pins.filter((pin) => pin.colId !== colId);
  next.push({ colId, pinned });
  return next;
}

/**
 * Builds a colId -> pinned lookup for merging saved pins onto column definitions.
 */
export function toPinMap(pins: StoredPin[]): Record<string, PinnedSide> {
  return pins.reduce<Record<string, PinnedSide>>((acc, pin) => {
    acc[pin.colId] = pin.pinned;
    return acc;
  }, {});
}
